import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { appConfirm } from '../../../lib/dialogs';
import { beginDialogAction } from '../../../lib/dialogAction';

// Race-admin action: move a signup (typically from a "Venteliste" class)
// into another class of the same race. The backend claims a capacity slot,
// copies the amounts from the target class and e-mails the contestant.
@observer
class MoveToRaceClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            movingTo: 0,
            movedTo: 0,
            errorCode: null,
        };
    }

    loadTargets = async () => {
        const { raceClassContestantStore } = this.props.stores;
        const { raceClassContestant } = raceClassContestantStore;
        if (raceClassContestant.race > 0) {
            await raceClassContestantStore.loadMoveTargets(raceClassContestant.race);
        }
    }

    move = async (e) => {
        const dialogAction = beginDialogAction(this, 'move', () => [
            this.props?.stores?.raceClassContestantStore?.raceClassContestant?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const { raceclass, title } = e.target.closest('button').dataset;
            const { callback = () => {} } = this.props;
            const { raceClassContestantStore } = this.props.stores;
            const { raceClassContestant } = raceClassContestantStore;
            const targetId = parseInt(raceclass, 10);
            if (!((await appConfirm(`Move ${raceClassContestant.firstname} ${raceClassContestant.lastname} to ${title}?`)) && dialogAction.isCurrent())) {
                return;
            }
            this.setState({ movingTo: targetId, movedTo: 0, errorCode: null });
            const response = await raceClassContestantStore.moveToRaceClass(raceClassContestant.id, targetId);
            if (response.status === 200) {
                this.setState({ movingTo: 0, movedTo: targetId });
                await this.loadTargets();
                callback();
                return;
            }
            this.setState({ movingTo: 0, errorCode: response.errorCode || 'MOVE_FAILED' });
        } finally {
            dialogAction.finish();
        }
    }

    componentDidMount() {
        this.loadTargets();
    }

    render() {
        const { raceClassContestantStore } = this.props.stores;
        const { raceClassContestant, moveTargetRaceClasses = [] } = raceClassContestantStore;
        const { movingTo, movedTo, errorCode } = this.state;
        const targets = moveTargetRaceClasses.filter(raceClass => raceClass.id !== raceClassContestant.raceClass);

        return (<>
            <div class='w-100 d-flex flex-column justify-content-start mt-4 px-3'>
                <div class='font-weight-lighter mb-1'>
                    <i class='fa-solid fa-arrow-right-arrow-left me-1' />
                    <Text id='race-class-contestants.move-title'>Move to race class</Text>
                </div>
                <small class='text-body-secondary mb-2'>
                    <Text id='race-class-contestants.move-help'>
                        Amounts are taken from the new class. When something is left to pay, the signup must be approved again after payment. The contestant gets an e-mail.
                    </Text>
                </small>
                <div class='d-flex flex-wrap gap-2'>
                    {targets.map(raceClass => {
                        const hasLimit = raceClass.maxMushers > 0;
                        const freeSeats = hasLimit ? raceClass.maxMushers - (raceClass.totalContestants || 0) : 0;
                        const isFull = hasLimit && freeSeats <= 0;
                        return (
                            <button
                                key={raceClass.id}
                                type='button'
                                class={`btn btn-sm rounded-pill ${isFull ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                                data-raceclass={raceClass.id}
                                data-title={raceClass.title}
                                disabled={isFull || movingTo > 0}
                                onClick={this.move}
                            >
                                {movingTo === raceClass.id
                                    ? <i class='fa-solid fa-spinner fa-spin me-1' />
                                    : <i class='fa-solid fa-user-plus me-1' />}
                                {raceClass.title}
                                {hasLimit && <span class='ms-1 text-body-secondary'>
                                    (<Text id='race-class-contestants.move-free-seats' fields={{ free: freeSeats, max: raceClass.maxMushers }}>
                                        {freeSeats} free of {raceClass.maxMushers}
                                    </Text>)
                                </span>}
                            </button>
                        );
                    })}
                </div>
                {movedTo > 0 && <div class='alert alert-success mt-2 mb-0 py-1' role='alert'>
                    <Text id='race-class-contestants.move-done'>The contestant has been moved. An e-mail with the next step has been sent.</Text>
                </div>}
                {errorCode === 'RACE_CLASS_FULL' && <div class='alert alert-danger mt-2 mb-0 py-1' role='alert'>
                    <Text id='race-class-contestants.move-error-full'>The race class is full.</Text>
                </div>}
                {errorCode && errorCode !== 'RACE_CLASS_FULL' && <div class='alert alert-danger mt-2 mb-0 py-1' role='alert'>
                    <Text id='race-class-contestants.move-error'>The move failed.</Text> ({errorCode})
                </div>}
            </div>
        </>);
    }
}

export default MoveToRaceClass;
